import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { InputNumber, Typography } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// UTILS
import {
  GENERIC_OUTPUT_FIRST,
  GENERIC_OUTPUT_LAST,
  isValidOutputNumber,
} from "../../../../utils/controlFloorRules";

/**
 * The output-number selector on a row whose command is `Control.GenericOutput`.
 *
 * That command's `position` is the controller's generic output, 1-32, not a
 * floor — so it gets this field where a floor-based row gets its floor strip.
 * Like the strip it is a SELECTOR: the row's own buttons send, and they stay
 * disabled until a valid number is entered, because the position-0 dispatch a
 * plain button would make is one VTS rejects.
 *
 * Deliberately no `min`/`max` on the input. Ant clamps an out-of-range entry on
 * blur, and the blur fires when the operator presses the command button — so
 * typing 40 would quietly send output 32. An entry outside 1-32 is kept on
 * screen, marked invalid, and reported upward as no choice at all.
 *
 * Entering a number never sends anything, whatever the deployment's firing mode:
 * typing "12" passes through "1" on the way, and a field that fired on change
 * would switch output 1 first.
 */
export default function ActionOutputNumberInput({
  value,
  onChange,
  disabled = false,
}: {
  /** The chosen output number, or null while none is entered. */
  value: number | null;
  /** Reports a valid output number, or null for an empty or invalid entry. */
  onChange: (output: number | null) => void;
  disabled?: boolean;
}) {
  const [t] = useTranslation("global");

  // What the operator typed, kept apart from the reported choice so an invalid
  // entry stays visible with its error instead of being replaced.
  const [typed, setTyped] = useState<number | null>(value);
  const invalid = typed !== null && !isValidOutputNumber(typed);

  const range = { first: GENERIC_OUTPUT_FIRST, last: GENERIC_OUTPUT_LAST };
  const label = t(
    "monitoringActions.selectOutput",
    "Output number ({{first}}-{{last}}) to send the command to",
    range,
  );

  const handleChange = (next: number | string | null): void => {
    const entered = typeof next === "number" ? next : null;
    setTyped(entered);
    onChange(isValidOutputNumber(entered) ? entered : null);
  };

  return (
    <div className="actionCard__floorSelect">
      <span className="actionCard__floorSelectLabel">{label}</span>
      <InputNumber
        value={typed}
        onChange={handleChange}
        status={invalid ? "error" : undefined}
        placeholder={`${GENERIC_OUTPUT_FIRST}-${GENERIC_OUTPUT_LAST}`}
        aria-label={label}
        aria-invalid={invalid}
        disabled={disabled}
        style={{ width: 120 }}
      />
      {invalid && (
        <Typography.Text type="danger" role="alert">
          {t(
            "monitoringActions.outputOutOfRange",
            "Enter a whole number from {{first}} to {{last}}.",
            range,
          )}
        </Typography.Text>
      )}
    </div>
  );
}
